import React, { FC, useState, useMemo } from "react";
import { Input } from '@brg/input';
import { useTranslation } from 'react-i18next';
import {BrgButton, Button, ButtonVariant, FlexBox, Fonts, useBreakpoint, useToasts} from '@brg/ui';
import { ComplaintAPI } from '@brg/api';
import { useAssociatedProfile } from 'core/auth';
import styled, { useTheme } from 'styled-components';
import { UInput } from "shared/components/inputs";
import {ApplicationShared, LayoutConst} from "../../../core";

export const ReportWrapper = styled(FlexBox)`
    width: 564px;
    margin: 0 auto 134px auto;
    justify-content: space-between;

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: calc(100% - 72px);
    margin: 64px 36px 0;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    width: calc(100% - 32px);
    margin: 32px 16px 0;
  }
`;

export const ButtonWrapper = styled(FlexBox)`

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: 100%;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    position: absolute;
    bottom: 16px;
  }
`;

export const ReportTitle = styled(FlexBox)`
    ${Fonts.Inter.sectionTitle};
    color: ${(p) => p.theme.reportForm.text};
`;

export const ReportFieldLabel = styled(FlexBox)`
    ${Fonts.Inter.smallText};
    font-size: 17px;
    line-height: 22px;
    letter-spacing: -0.02em;
    color: ${(p) => p.theme.reportForm.text};
    font-weight: 400;
  
    @media (max-width: ${LayoutConst.screen.s}px) {
      font-size: 13px;
      line-height: 16px;
      letter-spacing: normal;
    }
`;

const ReportButton = styled(BrgButton)`
  
    background: ${({theme}) => theme.reportForm.btns.submit.bg}!important;
    color: ${({theme}) => theme.reportForm.btns.submit.color}!important;
    border: none!important;
    border-radius: 3px!important;
  
    &:disabled {
      background: ${({theme}) => theme.reportForm.btns.submit.disabledBg}!important;
      color: ${({theme}) => theme.reportForm.btns.submit.disabledColor}!important;
      border: none!important;
    }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: calc(100vw - 32px);
  }
`

const ReportTextarea = styled(Input.Textarea)`
  background-color: ${({theme}) => theme.reportForm.inputs.backgroundColor};
  border-color: ${({theme}) => theme.reportForm.inputs.borderColor};
  border: none!important;
  color: ${({theme}) => theme.reportForm.inputs.color};
  height: 200px!important;
  
  &:focus {
    border: none!important;
  }

  ::-webkit-scrollbar {
    width: 3px; /* ширина scrollbar */
    height: 3px;
  }
  ::-webkit-scrollbar-track {
    background: ${({theme}) => theme.reportForm.inputs.backgroundColor}; /* цвет дорожки */
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 310px; /* закругления плашки */
    background: ${({theme}) => theme.reportForm.scroll};
  }
`

export interface ReportFormProps {
  entityId: string,
  entityType: string,
  handleClose: () => void,
  additionalInfo?: string
}

/**
 * **ReportForm** - форма отправки жалобы
 *
 * @param props
 */
export const ReportForm: FC<ReportFormProps> = (props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { handleClose, entityId, entityType, additionalInfo } = props;
  const [reasonType, setReasonType] = useState<number>(-1);
  const [comment, setComment] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(false);
  const profile = useAssociatedProfile();
  const is744BP = useBreakpoint(LayoutConst.screen.s)
  const toasts = useToasts();

  const onClickHandler = async (ev: React.MouseEvent) => {
    if (reasonType < 0 || reasonType > 13)
      return;

    setDisabled(true);

    try {
      await ComplaintAPI.DoReport({
        authorId: profile?.accountId,
        target: {
            entityId: entityId,
            entityType: entityType,
            service: ApplicationShared.application,
        },
        info: [comment.trim(), additionalInfo?.trim()].filter(Boolean).join("\n\n"),
        reason: reasonType as any
      });

      toasts.info(t('reports.report_success'));

      if (handleClose)
        handleClose();
    } catch (error) {
      toasts.error(t('reports.report_error'));
      console.error(error);
      setDisabled(false);
    }
  };


  const custom = useMemo(
    () => ({
      customTextColor: theme.reportForm.text,
      customPlaceholderColor: theme.reportForm.text,
      customBackgroundColor: theme.reportForm.inputs.dropdown.backgroundColor,
      customBackgroundColorHover: theme.reportForm.inputs.dropdown.backgroundColorHover,
      customBorderColor: 'transparent',
      customBorderColorHover: 'transparent',
      customScrollColor: theme.reportForm.inputs.dropdown.scroll,
      customShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    }),
    [theme]
  );

  const options = [
      { label: t('reports.reasons.other'), value: 0 },
      { label: t('reports.reasons.spam'), value: 1 },
      { label: t('reports.reasons.breakRules'), value: 2 },
      { label: t('reports.reasons.notThisPerson'), value: 3 },
      { label: t('reports.reasons.adultContent'), value: 4 },
      { label: t('reports.reasons.childPornograpy'), value: 5 },
      { label: t('reports.reasons.drugAdvocacy'), value: 6 },
      { label: t('reports.reasons.violence'), value: 7 },
      { label: t('reports.reasons.animalAbuse'), value: 8 },
      { label: t('reports.reasons.misleadingInformation'), value: 9 },
      { label: t('reports.reasons.fraud'), value: 10 },
      { label: t('reports.reasons.extremism'), value: 11 },
      { label: t('reports.reasons.copiedContent'), value: 12 },
      { label: t('reports.reasons.translation'), value: 13 },
  ];

  return (
    <ReportWrapper>
      <FlexBox width={'100%'} direction={'column'} directionMobile={'column'}>
        <FlexBox direction={'column'} directionMobile={'column'} offsetBottom={32} offsetBottomMobile={24}>
          <ReportFieldLabel offsetBottom={16} offsetBottomMobile={8}>{t('reports.reason')}</ReportFieldLabel>
          <UInput.Dropdown
            custom={custom}
            isSearchable={false}
            options={options}
            defaultValue={reasonType}
            onChange={(e: { label: any; value: number }) => setReasonType(e.value)}
            placeholder={t('reports.reason_placeholder')}
          />
        </FlexBox>
        <FlexBox direction={'column'} directionMobile={'column'} offsetBottom={32} offsetBottomMobile={24}>
          <ReportFieldLabel offsetBottom={16} offsetBottomMobile={8}>{t('reports.comment')}</ReportFieldLabel>
          <ReportTextarea
            rows={5}
            onChange={(value: any) => setComment(value.target.value)}
            value={comment}
          />
        </FlexBox>
        <ButtonWrapper justify={'end'} offsetBottom={0} offsetBottomMobile={0}>
          <FlexBox widthMobile="100%" justifyMobile={is744BP ? 'start' : 'end'}>
            <ReportButton
              variant={ButtonVariant.Filled}
              onClick={onClickHandler}
              disabled={disabled || reasonType < 0 || reasonType > 13}>
              {t('reports.button_send')}
            </ReportButton>
          </FlexBox>
        </ButtonWrapper>
      </FlexBox>
    </ReportWrapper>
  );
};import React, { FC, useState, useMemo } from "react";
import { Input } from '@brg/input';
import { useTranslation } from 'react-i18next';
import {BrgButton, Button, ButtonVariant, FlexBox, Fonts, useBreakpoint, useToasts} from '@brg/ui';
import { ComplaintAPI } from '@brg/api';
import { useAssociatedProfile } from 'core/auth';
import styled, { useTheme } from 'styled-components';
import { UInput } from "shared/components/inputs";
import {ApplicationShared, LayoutConst} from "../../../core";

export const ReportWrapper = styled(FlexBox)`
    width: 564px;
    margin: 0 auto 134px auto;
    justify-content: space-between;

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: calc(100% - 72px);
    margin: 64px 36px 0;
  }
  @media (max-width: ${LayoutConst.screen.s}px) {
    width: calc(100% - 32px);
    margin: 32px 16px 0;
  }
`;

export const ButtonWrapper = styled(FlexBox)`

  @media (max-width: ${LayoutConst.screen.md}px) {
    width: 100%;
  }
  
  @media (max-width: ${LayoutConst.screen.s}px) {
    position: absolute;
    bottom: 16px;
  }
`;

export const ReportTitle = styled(FlexBox)`
    ${Fonts.Inter.sectionTitle};
    color: ${(p) => p.theme.reportForm.text};
`;

export const ReportFieldLabel = styled(FlexBox)`
    ${Fonts.Inter.smallText};
    font-size: 17px;
    line-height: 22px;
    letter-spacing: -0.02em;
    color: ${(p) => p.theme.reportForm.text};
    font-weight: 400;
  
    @media (max-width: ${LayoutConst.screen.s}px) {
      font-size: 13px;
      line-height: 16px;
      letter-spacing: normal;
    }
`;

const ReportButton = styled(BrgButton)`
  
    background: ${({theme}) => theme.reportForm.btns.submit.bg}!important;
    color: ${({theme}) => theme.reportForm.btns.submit.color}!important;
    border: none!important;
    border-radius: 3px!important;
  
    &:disabled {
      background: ${({theme}) => theme.reportForm.btns.submit.disabledBg}!important;
      color: ${({theme}) => theme.reportForm.btns.submit.disabledColor}!important;
      border: none!important;
    }

  @media (max-width: ${LayoutConst.screen.s}px) {
    width: calc(100vw - 32px);
  }
`

const ReportTextarea = styled(Input.Textarea)`
  background-color: ${({theme}) => theme.reportForm.inputs.backgroundColor};
  border-color: ${({theme}) => theme.reportForm.inputs.borderColor};
  border: none!important;
  color: ${({theme}) => theme.reportForm.inputs.color};
  height: 200px!important;
  
  &:focus {
    border: none!important;
  }

  ::-webkit-scrollbar {
    width: 3px; /* ширина scrollbar */
    height: 3px;
  }
  ::-webkit-scrollbar-track {
    background: ${({theme}) => theme.reportForm.inputs.backgroundColor}; /* цвет дорожки */
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 310px; /* закругления плашки */
    background: ${({theme}) => theme.reportForm.scroll};
  }
`

export interface ReportFormProps {
  entityId: string,
  entityType: string,
  handleClose: () => void,
  additionalInfo?: string
}

/**
 * **ReportForm** - форма отправки жалобы
 *
 * @param props
 */
export const ReportForm: FC<ReportFormProps> = (props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { handleClose, entityId, entityType, additionalInfo } = props;
  const [reasonType, setReasonType] = useState<number>(-1);
  const [comment, setComment] = useState<string>("");
  const [disabled, setDisabled] = useState<boolean>(false);
  const profile = useAssociatedProfile();
  const is744BP = useBreakpoint(LayoutConst.screen.s)
  const toasts = useToasts();

  const onClickHandler = async (ev: React.MouseEvent) => {
    if (reasonType < 0 || reasonType > 13)
      return;

    setDisabled(true);

    try {
      await ComplaintAPI.DoReport({
        authorId: profile?.accountId,
        target: {
            entityId: entityId,
            entityType: entityType,
            service: ApplicationShared.application,
        },
        info: [comment.trim(), additionalInfo?.trim()].filter(Boolean).join("\n\n"),
        reason: reasonType as any
      });

      toasts.info(t('reports.report_success'));

      if (handleClose)
        handleClose();
    } catch (error) {
      toasts.error(t('reports.report_error'));
      console.error(error);
      setDisabled(false);
    }
  };


  const custom = useMemo(
    () => ({
      customTextColor: theme.reportForm.text,
      customPlaceholderColor: theme.reportForm.text,
      customBackgroundColor: theme.reportForm.inputs.dropdown.backgroundColor,
      customBackgroundColorHover: theme.reportForm.inputs.dropdown.backgroundColorHover,
      customBorderColor: 'transparent',
      customBorderColorHover: 'transparent',
      customScrollColor: theme.reportForm.inputs.dropdown.scroll,
      customShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    }),
    [theme]
  );

  const options = [
      { label: t('reports.reasons.other'), value: 0 },
      { label: t('reports.reasons.spam'), value: 1 },
      { label: t('reports.reasons.breakRules'), value: 2 },
      { label: t('reports.reasons.notThisPerson'), value: 3 },
      { label: t('reports.reasons.adultContent'), value: 4 },
      { label: t('reports.reasons.childPornograpy'), value: 5 },
      { label: t('reports.reasons.drugAdvocacy'), value: 6 },
      { label: t('reports.reasons.violence'), value: 7 },
      { label: t('reports.reasons.animalAbuse'), value: 8 },
      { label: t('reports.reasons.misleadingInformation'), value: 9 },
      { label: t('reports.reasons.fraud'), value: 10 },
      { label: t('reports.reasons.extremism'), value: 11 },
      { label: t('reports.reasons.copiedContent'), value: 12 },
      { label: t('reports.reasons.translation'), value: 13 },
  ];

  return (
    <ReportWrapper>
      <FlexBox width={'100%'} direction={'column'} directionMobile={'column'}>
        <FlexBox direction={'column'} directionMobile={'column'} offsetBottom={32} offsetBottomMobile={24}>
          <ReportFieldLabel offsetBottom={16} offsetBottomMobile={8}>{t('reports.reason')}</ReportFieldLabel>
          <UInput.Dropdown
            custom={custom}
            isSearchable={false}
            options={options}
            defaultValue={reasonType}
            onChange={(e: { label: any; value: number }) => setReasonType(e.value)}
            placeholder={t('reports.reason_placeholder')}
          />
        </FlexBox>
        <FlexBox direction={'column'} directionMobile={'column'} offsetBottom={32} offsetBottomMobile={24}>
          <ReportFieldLabel offsetBottom={16} offsetBottomMobile={8}>{t('reports.comment')}</ReportFieldLabel>
          <ReportTextarea
            rows={5}
            onChange={(value: any) => setComment(value.target.value)}
            value={comment}
          />
        </FlexBox>
        <ButtonWrapper justify={'end'} offsetBottom={0} offsetBottomMobile={0}>
          <FlexBox widthMobile="100%" justifyMobile={is744BP ? 'start' : 'end'}>
            <ReportButton
              variant={ButtonVariant.Filled}
              onClick={onClickHandler}
              disabled={disabled || reasonType < 0 || reasonType > 13}>
              {t('reports.button_send')}
            </ReportButton>
          </FlexBox>
        </ButtonWrapper>
      </FlexBox>
    </ReportWrapper>
  );
};